/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { hasValidRecAccount, isRegisteredUserLoggedIn } from 'sarsaparilla/auth';

import {
    openLoginModal,
    openLoginWithGuestModal,
    openForgotPasswordModal,
    closeAuthModal,
    type AuthModalState,
} from './authModal.slice';

/** Options for {@link AuthContextValue.requireLogin}. */
export interface RequireLoginOptions {
    /**
     * When `true`, open the login modal that also offers "Continue as a Guest".
     * A successful guest login resolves the promise with `true`. Defaults to
     * `false` (standard login modal, no guest option).
     */
    allowGuest?: boolean;
}

export interface AuthContextValue {
    openLoginModal: () => void;
    openLoginWithGuestModal: () => void;
    openForgotPasswordModal: () => void;
    closeAuthModal: () => void;
    /**
     * Ensure the user is logged in before continuing an action.
     *
     * - If already logged in, resolves `true` immediately without opening a modal.
     * - Otherwise opens the login modal and resolves once the flow finishes:
     *   `true` when the user logs in (password, MFA, create-account, or guest),
     *   `false` when the user closes/cancels the modal.
     *
     * Concurrent calls while a request is in flight share the same promise.
     *
     * @example
     * const { requireLogin } = useAuth();
     * if (await requireLogin()) {
     *     addToCart(item);
     * }
     */
    requireLogin: (options?: RequireLoginOptions) => Promise<boolean>;
}

export const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

/**
 * Minimal slice of redux state this provider observes to detect login
 * completion. Redux is kept as an internal implementation detail; consumers
 * only interact through {@link useAuth}.
 */
interface AuthProviderReduxState {
    auth?: { account?: unknown };
    authModal?: AuthModalState;
}

interface PendingLoginRequest {
    promise: Promise<boolean>;
    resolve: (loggedIn: boolean) => void;
}

export function useAuth() {
    const context = React.useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth must be used within an AuthContext');
    }
    return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const dispatch = useDispatch();

    const isLoggedIn = useSelector((state: AuthProviderReduxState) =>
        Boolean(state.auth?.account)
    );
    const screen = useSelector(
        (state: AuthProviderReduxState) => state.authModal?.screen ?? null
    );

    // A single, app-wide pending login request. Held in a ref so that opening
    // the modal (which only mutates the ref) does not cause a re-render, and so
    // concurrent callers can share the same in-flight promise.
    const pendingRef = React.useRef<PendingLoginRequest | null>(null);

    // Resolve the pending request when the relevant auth state transitions.
    React.useEffect(() => {
        const pending = pendingRef.current;
        if (!pending) {
            return;
        }

        if (isLoggedIn) {
            // Success — covers password, MFA, create-account, and guest flows,
            // since all of them ultimately populate the logged-in account.
            pendingRef.current = null;
            pending.resolve(true);
        } else if (!screen) {
            // The modal closed while still logged out — the user cancelled.
            pendingRef.current = null;
            pending.resolve(false);
        }
        // Any non-null screen (login, mfaCode, createAccount, requestPasswordReset)
        // means the user is still mid-flow, so keep waiting.
    }, [isLoggedIn, screen]);

    // If AuthProvider unmounts while a login request is in flight, resolve
    // false so consumers aren't left awaiting an unresolved promise.
    // This is essentially a no-op because it would mean the entire app is being torn down,
    // but it's a good defensive programming practice
    React.useEffect(() => {
        return () => {
            if (pendingRef.current) {
                pendingRef.current.resolve(false);
                pendingRef.current = null;
            }
        };
    }, []);

    const requireLogin = React.useCallback(
        (options?: RequireLoginOptions): Promise<boolean> => {
            // Already logged in: resolve immediately using the same predicate the
            // modal slice gates on, so we never open a no-op modal.
            if (isRegisteredUserLoggedIn()) {
                return Promise.resolve(true);
            }

            if (options?.allowGuest && hasValidRecAccount()) {
                return Promise.resolve(true);
            }

            // A request is already in flight: share its promise.
            if (pendingRef.current) {
                return pendingRef.current.promise;
            }

            let resolveFn: (loggedIn: boolean) => void = () => {};
            const promise = new Promise<boolean>((resolve) => {
                resolveFn = resolve;
            });
            pendingRef.current = { promise, resolve: resolveFn };

            if (options?.allowGuest) {
                dispatch(openLoginWithGuestModal());
            } else {
                dispatch(openLoginModal());
            }

            return promise;
        },
        [dispatch]
    );

    const value = React.useMemo(() => {
        return {
            openLoginModal: () => {
                dispatch(openLoginModal());
            },
            openLoginWithGuestModal: () => {
                dispatch(openLoginWithGuestModal());
            },
            openForgotPasswordModal: () => {
                dispatch(openForgotPasswordModal());
            },
            closeAuthModal: () => {
                dispatch(closeAuthModal());
            },
            requireLogin,
        };
    }, [dispatch, requireLogin]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
